import React, { Component } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  CustomInput,
} from "reactstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import axios from "axios";
import Select from "react-select";
import { END_POINT } from "../../../constants/defaultValues";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";

// Helper for permission labels
// Helper for permission labels
const getPermissionLabel = (perm, intl) => {
  const id = `settings.partner.perm.${perm.replace(":", "-")}`;
  return intl.formatMessage({ id, defaultMessage: perm });
};

/**
 * Partner Management Settings
 * Quản lý API Partners và API Keys
 */
class PartnerManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      partners: [],
      analytics: null,
      logs: [],

      // Modal states
      showCreateModal: false,
      showKeyModal: false,
      showKeyResultModal: false,

      // Form data
      newPartner: {
        partner_name: "",
        partner_type: "other",
        key_environment: "test",
        permissions: ["orders:read", "products:read"],
        rate_limit: 1000,
        webhook_url: "",
        contact_email: "",
        description: "",
      },

      // New key result (shown only once)
      newKeyResult: null,

      // Selected partner for key creation
      selectedPartner: null,

      // Error/success messages
      error: null,
      success: null,
      copySuccess: false,

      // Confirmation modal
      showConfirmModal: false,
      confirmModalTitle: "",
      confirmModalMessage: "",
      pendingAction: null, // { type: 'regenerate' | 'revoke', keyId: number }
    };

    this.scrollRef = null;
    this.handleWheel = this.handleWheel.bind(this);
    this.setScrollRef = this.setScrollRef.bind(this);
  }

  setScrollRef(el) {
    if (this.scrollRef) {
      const oldContainer = this.scrollRef._container || this.scrollRef;
      if (oldContainer && oldContainer.removeEventListener) {
        oldContainer.removeEventListener("wheel", this.handleWheel);
      }
    }

    this.scrollRef = el;

    if (this.scrollRef) {
      const container = this.scrollRef._container || this.scrollRef;
      if (container && container.addEventListener) {
        container.addEventListener("wheel", this.handleWheel, {
          passive: false,
        });
      }
    }
  }

  handleWheel(e) {
    if (e.deltaY !== 0) {
      e.currentTarget.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }

  componentDidMount() {
    // Only load data if user has API permission (pro_auto or higher)
    if (this.checkPermission("pro_auto")) {
      this.loadPartners();
      this.loadAnalytics();

      // Polling analytics every 5s
      this.analyticsInterval = setInterval(this.loadAnalytics, 5000);
    }
  }

  componentWillUnmount() {
    if (this.analyticsInterval) {
      clearInterval(this.analyticsInterval);
    }
  }

  loadPartners = async () => {
    // Check permission first to avoid 403 error
    if (!this.checkPermission("pro_auto")) {
      return;
    }
    this.setState({ loading: true });
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/api/partner-management/partners`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      const partnersData = (res.data.data || []).map((partner) => ({
        ...partner,
        apiKeys: (partner.apiKeys || []).filter(
          (key) => key.is_active === true || key.is_active === 1,
        ),
      }));

      this.setState({ partners: partnersData, loading: false });
    } catch (error) {
      console.error("Load partners error:", error);
      this.setState({
        loading: false,
        error: "",
      });
    }
  };

  loadAnalytics = async () => {
    // Check permission first to avoid 403 error
    if (!this.checkPermission("pro_auto")) {
      return;
    }
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/api/partner-management/analytics`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      this.setState({ analytics: res.data.data });
    } catch (error) {
      console.error("Load analytics error:", error);
    }
  };

  checkPermission = (requiredPackage) => {
    const { license } = this.props;
    if (!license) return false;
    const ranks = {
      free: 0,
      basic: 1,
      pro: 2,
      pro_auto: 3,
      enterprise: 4,
    };
    const currentPackage = (license.purchased_package || "free").toLowerCase();
    const currentRank = ranks[currentPackage] || 0;
    const requiredRank = ranks[requiredPackage] || 0;

    if (currentRank > 0 && license.expired_date) {
      const today = new Date().toISOString().split("T")[0];
      if (license.expired_date < today) {
        return 0 >= requiredRank;
      }
    }
    return currentRank >= requiredRank;
  };

  handleCreatePartner = async () => {
    const { newPartner } = this.state;

    if (!newPartner.partner_name.trim()) {
      this.setState({
        error: this.props.intl.formatMessage({
          id: "settings.partner.alert.create-error",
        }),
      });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/api/partner-management/partners`,
        newPartner,
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      // Show the API key result (only shown once!)
      this.setState({
        newKeyResult: res.data.data.apiKey,
        showCreateModal: false,
        showKeyResultModal: true,
        loading: false,
        success: this.props.intl.formatMessage({
          id: "settings.partner.alert.create-success",
        }),
        newPartner: {
          partner_name: "",
          partner_type: "other",
          key_environment: "test",
          permissions: ["orders:read", "products:read"],
          rate_limit: 1000,
          webhook_url: "",
          contact_email: "",
          description: "",
        },
      });

      this.loadPartners();
    } catch (error) {
      console.error("Create partner error:", error);
      this.setState({
        loading: false,
        error:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          this.props.intl.formatMessage({
            id: "settings.partner.alert.create-fail",
          }),
      });
    }
  };

  handleCreateKey = async (partnerId) => {
    this.setState({ loading: true, error: null });

    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/api/partner-management/partners/${partnerId}/keys`,
        {
          name: "New API Key",
          environment: "test",
        },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      this.setState({
        newKeyResult: res.data.data,
        showKeyResultModal: true,
        loading: false,
      });

      this.loadPartners();
    } catch (error) {
      console.error("Create key error:", error);
      this.setState({
        loading: false,
        error:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          this.props.intl.formatMessage({
            id: "settings.partner.alert.create-fail",
          }),
      });
    }
  };

  // Show confirmation modal for revoke action
  handleRevokeKey = (keyId) => {
    this.setState({
      showConfirmModal: true,
      confirmModalTitle: this.props.intl.formatMessage({
        id: "settings.partner.alert.revoke-title",
      }),
      confirmModalMessage: this.props.intl.formatMessage({
        id: "settings.partner.alert.revoke-confirm",
      }),
      pendingAction: { type: "revoke", keyId },
    });
  };

  // Actually perform revoke after confirmation
  executeRevokeKey = async (keyId) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      await axios.delete(`${END_POINT}/api/partner-management/keys/${keyId}`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      this.setState({
        success: this.props.intl.formatMessage({
          id: "settings.partner.alert.revoke-success",
        }),
      });
      this.loadPartners();
    } catch (error) {
      console.error("Revoke key error details:", error.response?.data || error);
      this.setState({
        error:
          this.props.intl.formatMessage({
            id: "settings.partner.alert.revoke-fail",
          }) + (error.response?.data?.message || error.message),
      });
    }
  };

  // Show confirmation modal for regenerate action
  handleRegenerateKey = (keyId) => {
    this.setState({
      showConfirmModal: true,
      confirmModalTitle: this.props.intl.formatMessage({
        id: "settings.partner.alert.regenerate-title",
      }),
      confirmModalMessage: this.props.intl.formatMessage({
        id: "settings.partner.alert.regenerate-confirm",
      }),
      pendingAction: { type: "regenerate", keyId },
    });
  };

  // Actually perform regenerate after confirmation
  executeRegenerateKey = async (keyId) => {
    this.setState({ loading: true, error: null });

    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/api/partner-management/keys/${keyId}/rotate`,
        {},
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );

      this.setState({
        newKeyResult: res.data.data.new_key,
        showKeyResultModal: true,
        loading: false,
        success: this.props.intl.formatMessage({
          id: "settings.partner.alert.regenerate-success",
        }),
      });

      this.loadPartners();
    } catch (error) {
      console.error("Regenerate key error:", error);
      this.setState({
        loading: false,
        error:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          this.props.intl.formatMessage({
            id: "settings.partner.alert.regenerate-fail",
          }),
      });
    }
  };

  // Handle confirmation modal confirm button
  handleConfirmAction = () => {
    const { pendingAction } = this.state;
    this.setState({ showConfirmModal: false, pendingAction: null });

    if (pendingAction) {
      if (pendingAction.type === "regenerate") {
        this.executeRegenerateKey(pendingAction.keyId);
      } else if (pendingAction.type === "revoke") {
        this.executeRevokeKey(pendingAction.keyId);
      }
    }
  };

  // Handle confirmation modal cancel button
  handleCancelConfirm = () => {
    this.setState({ showConfirmModal: false, pendingAction: null });
  };

  handleTogglePartner = async (partnerId, isActive) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      await axios.patch(
        `${END_POINT}/api/partner-management/partners/${partnerId}`,
        { is_active: !isActive },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );
      this.loadPartners();
    } catch (error) {
      console.error("Toggle partner error:", error);
      this.setState({
        error: this.props.intl.formatMessage({
          id: "settings.partner.alert.update-fail",
        }),
      });
    }
  };

  copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    this.setState({ copySuccess: true });
    setTimeout(() => this.setState({ copySuccess: false }), 2000);
  };

  renderAnalytics() {
    const { analytics } = this.state;

    if (!analytics) return null;

    return (
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <CardBody>
              <h3 className="text-primary">{analytics.total_requests || 0}</h3>
              <small className="text-muted">
                <IntlMessages id="settings.partner.analytics.total" />
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <CardBody>
              <h3 className="text-success">{analytics.requests_today || 0}</h3>
              <small className="text-muted">
                <IntlMessages id="settings.partner.analytics.today" />
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <CardBody>
              <h3 className="text-info">{analytics.success_rate || 0}%</h3>
              <small className="text-muted">
                <IntlMessages id="settings.partner.analytics.success-rate" />
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <CardBody>
              <h3 className="text-warning">
                {analytics.avg_response_time || 0}ms
              </h3>
              <small className="text-muted">
                <IntlMessages id="settings.partner.analytics.avg-time" />
              </small>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  renderPartnersList() {
    const { partners, loading } = this.state;

    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="loading" />
        </div>
      );
    }

    if (partners.length === 0) {
      return (
        <Alert color="info">
          <IntlMessages id="settings.partner.alert.empty" />
        </Alert>
      );
    }
    return (
      <PerfectScrollbar
        ref={this.setScrollRef}
        options={{ suppressScrollY: true, wheelPropagation: false }}
      >
        <Table responsive hover>
          <thead>
            <tr>
              <th>
                <IntlMessages id="settings.partner.table.name" />
              </th>
              <th>
                <IntlMessages id="settings.partner.table.keys" />
              </th>
              <th>
                <IntlMessages id="settings.partner.table.permissions" />
              </th>
              <th>
                <IntlMessages id="settings.partner.table.note" />
              </th>
              {/* <th>Rate Limit</th> */}
              <th>
                <IntlMessages id="settings.partner.table.action" />
              </th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner) => (
              <tr key={partner.id}>
                <td className="align-middle">
                  <strong>{partner.partner_name}</strong>
                  {partner.contact_email && (
                    <div className="small text-muted">
                      {partner.contact_email}
                    </div>
                  )}
                </td>
                <td className="align-middle">
                  {partner.apiKeys &&
                    partner.apiKeys
                      .filter((key) => key.is_active)
                      .map((key) => (
                        <div key={key.id} className="mb-1">
                          <Badge
                            color={key.is_active ? "success" : "danger"}
                            className="mr-1"
                          >
                            {key.environment}
                          </Badge>
                          <small className="text-muted">{key.name}</small>
                        </div>
                      ))}
                </td>
                <td className="align-middle" style={{ maxWidth: "200px" }}>
                  {partner.permissions &&
                    partner.permissions.map((perm) => (
                      <Badge key={perm} color="info" className="mr-1 mb-1">
                        {getPermissionLabel(perm, this.props.intl)}
                      </Badge>
                    ))}
                </td>
                <td className="align-middle">
                  <span className="small text-muted">
                    {partner.description || "-"}
                  </span>
                </td>
                {/* <td className="align-middle">{partner.rate_limit}/giờ</td> */}
                <td className="align-middle">
                  {/* Show regenerate and delete buttons for keys */}
                  {partner.apiKeys &&
                    partner.apiKeys
                      .filter((k) => k.is_active)
                      .map((key) => (
                        <div key={key.id} className="d-flex">
                          <Button
                            size="sm"
                            color="outline-primary"
                            className="mr-1"
                            style={{ minWidth: "40px" }}
                            onClick={() => this.handleRegenerateKey(key.id)}
                            title={this.props.intl.formatMessage(
                              { id: "settings.partner.alert.regenerate-title" },
                              { name: key.name },
                            )}
                          >
                            <i className="simple-icon-refresh" />
                          </Button>
                          <Button
                            size="sm"
                            color="outline-danger"
                            style={{ minWidth: "40px" }}
                            onClick={() => this.handleRevokeKey(key.id)}
                            title={`${this.props.intl.formatMessage({ id: "settings.partner.alert.revoke-title" })}: ${key.name}`}
                          >
                            <i className="simple-icon-trash" />
                          </Button>
                        </div>
                      ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </PerfectScrollbar>
    );
  }

  renderCreateModal() {
    const { showCreateModal, newPartner, loading } = this.state;

    const partnerTypes = [
      { value: "shipping", label: "Vận chuyển" },
      { value: "payment", label: "Thanh toán" },
      { value: "accounting", label: "Kế toán" },
      { value: "marketing", label: "Marketing/CRM" },
      { value: "other", label: "Khác" },
    ];

    const permissions = [
      {
        value: "orders:read",
        label: getPermissionLabel("orders:read", this.props.intl),
      },
      {
        value: "orders:write",
        label: getPermissionLabel("orders:write", this.props.intl),
      },
      {
        value: "products:read",
        label: getPermissionLabel("products:read", this.props.intl),
      },
      {
        value: "products:write",
        label: getPermissionLabel("products:write", this.props.intl),
      },
      {
        value: "inventory:read",
        label: getPermissionLabel("inventory:read", this.props.intl),
      },
      {
        value: "inventory:write",
        label: getPermissionLabel("inventory:write", this.props.intl),
      },
      {
        value: "recipes:read",
        label: getPermissionLabel("recipes:read", this.props.intl),
      },
      {
        value: "recipes:write",
        label: getPermissionLabel("recipes:write", this.props.intl),
      },
      {
        value: "customers:read",
        label: getPermissionLabel("customers:read", this.props.intl),
      },
      {
        value: "customers:write",
        label: getPermissionLabel("customers:write", this.props.intl),
      },
      {
        value: "user:read",
        label: getPermissionLabel("user:read", this.props.intl),
      },
    ];

    return (
      <Modal
        isOpen={showCreateModal}
        toggle={() => this.setState({ showCreateModal: false })}
        size="lg"
      >
        <ModalHeader toggle={() => this.setState({ showCreateModal: false })}>
          <IntlMessages id="settings.partner.modal.create.title" />
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>
                <IntlMessages id="settings.partner.modal.create.name" />
              </Label>
              <Input
                type="text"
                value={newPartner.partner_name}
                onChange={(e) =>
                  this.setState({
                    newPartner: {
                      ...newPartner,
                      partner_name: e.target.value,
                    },
                  })
                }
                placeholder={this.props.intl.formatMessage({
                  id: "settings.partner.modal.create.name-placeholder",
                })}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <IntlMessages id="settings.partner.modal.create.permissions" />
              </Label>
              {/* Hiển thị các quyền đã chọn */}
              {newPartner.permissions.length > 0 && (
                <div className="mb-2">
                  {newPartner.permissions.map((perm) => {
                    const permObj = permissions.find((p) => p.value === perm);
                    return (
                      <Badge
                        key={perm}
                        color="primary"
                        className="mr-1 mb-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const perms = newPartner.permissions.filter(
                            (p) => p !== perm,
                          );
                          this.setState({
                            newPartner: { ...newPartner, permissions: perms },
                          });
                        }}
                      >
                        {permObj ? permObj.label : perm} ×
                      </Badge>
                    );
                  })}
                </div>
              )}
              <Select
                options={permissions.filter(
                  (p) => !newPartner.permissions.includes(p.value),
                )}
                placeholder={this.props.intl.formatMessage({
                  id: "settings.partner.modal.create.permissions-placeholder",
                })}
                onChange={(selected) => {
                  if (selected) {
                    this.setState({
                      newPartner: {
                        ...newPartner,
                        permissions: [
                          ...newPartner.permissions,
                          selected.value,
                        ],
                      },
                    });
                  }
                }}
                value={null}
                isClearable={false}
              />
              <small className="text-muted">
                <IntlMessages id="settings.partner.modal.create.permissions-desc" />
              </small>
            </FormGroup>

            <FormGroup>
              <Label>
                <IntlMessages id="settings.partner.modal.create.env" />
              </Label>
              <div>
                <CustomInput
                  type="radio"
                  id="envTest"
                  name="keyEnvironment"
                  label={this.props.intl.formatMessage({
                    id: "settings.partner.modal.create.env-test",
                  })}
                  checked={newPartner.key_environment === "test"}
                  onChange={() =>
                    this.setState({
                      newPartner: { ...newPartner, key_environment: "test" },
                    })
                  }
                  inline
                />
                <CustomInput
                  type="radio"
                  id="envLive"
                  name="keyEnvironment"
                  label={this.props.intl.formatMessage({
                    id: "settings.partner.modal.create.env-live",
                  })}
                  checked={newPartner.key_environment === "live"}
                  onChange={() =>
                    this.setState({
                      newPartner: { ...newPartner, key_environment: "live" },
                    })
                  }
                  inline
                />
              </div>
              <div
                className="mt-2 p-3"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  fontSize: "13px",
                }}
              >
                <div className="mb-2">
                  <strong style={{ color: "#f59e0b" }}>
                    🧪 <IntlMessages id="settings.partner.env.test-label" />
                  </strong>{" "}
                  <IntlMessages id="settings.partner.env.test-desc" />
                </div>
                <div>
                  <strong style={{ color: "#10b981" }}>
                    🚀 <IntlMessages id="settings.partner.env.live-label" />
                  </strong>{" "}
                  <IntlMessages id="settings.partner.env.live-desc" />
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <Label>
                <IntlMessages id="settings.partner.modal.create.desc" />
              </Label>
              <Input
                type="textarea"
                value={newPartner.description}
                onChange={(e) =>
                  this.setState({
                    newPartner: { ...newPartner, description: e.target.value },
                  })
                }
                rows={2}
                placeholder={this.props.intl.formatMessage({
                  id: "settings.partner.modal.create.desc-placeholder",
                })}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => this.setState({ showCreateModal: false })}
          >
            <IntlMessages id="settings.partner.modal.create.cancel" />
          </Button>
          <Button
            color="primary"
            onClick={this.handleCreatePartner}
            disabled={loading}
          >
            {loading ? (
              <IntlMessages id="settings.partner.modal.create.submitting" />
            ) : (
              <IntlMessages id="settings.partner.modal.create.submit" />
            )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderKeyResultModal() {
    const { showKeyResultModal, newKeyResult } = this.state;

    if (!newKeyResult) return null;

    return (
      <Modal
        isOpen={showKeyResultModal}
        toggle={() =>
          this.setState({ showKeyResultModal: false, newKeyResult: null })
        }
      >
        <ModalHeader
          toggle={() =>
            this.setState({ showKeyResultModal: false, newKeyResult: null })
          }
        >
          <IntlMessages id="settings.partner.modal.result.title" />
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong>
              <IntlMessages id="settings.partner.modal.result.important" />
            </strong>
          </Alert>

          <FormGroup>
            <Label>API Key</Label>
            <div className="d-flex">
              <Input
                type="text"
                value={newKeyResult.key}
                readOnly
                className="font-monospace"
              />
              <Button
                color="primary"
                className="ml-2"
                onClick={() => this.copyToClipboard(newKeyResult.key)}
              >
                {this.state.copySuccess ? (
                  <IntlMessages id="settings.partner.modal.result.copied" />
                ) : (
                  <IntlMessages id="settings.partner.modal.result.copy" />
                )}
              </Button>
            </div>
            {this.state.copySuccess && (
              <Alert color="success" className="mt-2 mb-0 py-2">
                <IntlMessages id="settings.partner.modal.result.copy-alert" />
              </Alert>
            )}
          </FormGroup>

          <div className="small text-muted">
            <p>
              <strong>Environment:</strong> {newKeyResult.environment}
            </p>
            <p>
              <strong>Name:</strong> {newKeyResult.name}
            </p>
            {newKeyResult.expires_at && (
              <p>
                <strong>
                  <IntlMessages id="settings.partner.modal.result.expires" />
                </strong>{" "}
                {new Date(newKeyResult.expires_at).toLocaleString()}
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() =>
              this.setState({ showKeyResultModal: false, newKeyResult: null })
            }
          >
            <IntlMessages id="settings.partner.modal.result.close" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  render() {
    const { error, success } = this.state;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>
            <IntlMessages id="settings.partner.title" />
          </h4>
          <div>
            <Button
              color="outline-info"
              className="mr-2"
              href={`${END_POINT}/api/public/docs`}
              target="_blank"
              style={{ marginRight: "10px" }}
            >
              <i className="simple-icon-book-open mr-2" />
              <IntlMessages id="settings.partner.doc-btn" />
            </Button>
            {!this.checkPermission("pro_auto") ? (
              <Button
                color="secondary"
                disabled
                title={this.props.intl.formatMessage({
                  id: "settings.partner.pro-feature-tooltip",
                })}
              >
                <i className="simple-icon-lock mr-2" />
                <IntlMessages id="settings.partner.create-btn-pro-auto" />
              </Button>
            ) : (
              <Button
                color="primary"
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <IntlMessages id="settings.partner.create-btn" />
              </Button>
            )}
          </div>
        </div>

        {!this.checkPermission("pro_auto") && (
          <Alert color="warning" className="mb-4">
            <IntlMessages id="settings.partner.upgrade-alert.text" />{" "}
            <strong>Pro Auto</strong>{" "}
            <IntlMessages id="settings.partner.upgrade-alert.action" />{" "}
            <a
              href="/app/applications/upgrade"
              className="alert-link font-weight-bold"
            >
              <IntlMessages id="settings.partner.upgrade-alert.link" />
            </a>
          </Alert>
        )}

        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            color="success"
            toggle={() => this.setState({ success: null })}
          >
            {success}
          </Alert>
        )}

        {this.renderAnalytics()}

        <Card>
          <CardBody>
            <h5 className="mb-3">Danh sách API Keys</h5>
            {this.renderPartnersList()}
          </CardBody>
        </Card>

        {this.renderCreateModal()}
        {this.renderKeyResultModal()}
        {this.renderConfirmModal()}
      </div>
    );
  }

  renderConfirmModal() {
    const { showConfirmModal, confirmModalTitle, confirmModalMessage } =
      this.state;

    return (
      <Modal isOpen={showConfirmModal} toggle={this.handleCancelConfirm}>
        <ModalHeader toggle={this.handleCancelConfirm}>
          ⚠️ {confirmModalTitle}
        </ModalHeader>
        <ModalBody>
          <p>{confirmModalMessage}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.handleCancelConfirm}>
            Hủy
          </Button>
          <Button color="danger" onClick={this.handleConfirmAction}>
            Xác nhận
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default injectIntl(PartnerManagement);
